import React, { FC } from 'react';

import { useTheme } from '@mui/material';
import { alpha, styled } from '@mui/material/styles';
import { useDragLayer } from 'react-dnd';

import { QuizColorsMode } from '~/blocks/TestCard/TestCard';
import {
  createAnswersThemes,
  Info,
  InfoRow,
  ItemWrapper,
  OrderLabel,
  StyledReorderIcon,
  StyledTitle,
} from '~/components/Test/OrderingQuestion/styles';
import useTenantTranslation from '~/hooks/useTenantTranslation';

interface CustomDragLayerProps {
  width?: number;
  answerOrder: string[];
  isSmall?: boolean;
  colorsMode?: QuizColorsMode;
}

const CustomDragLayer: FC<CustomDragLayerProps> = ({ width, answerOrder, isSmall, colorsMode }) => {
  const theme = useTheme();
  const { t } = useTenantTranslation();

  const { isDragging, item, currentOffset } = useDragLayer((monitor) => ({
    item: monitor.getItem(),
    itemType: monitor.getItemType(),
    initialOffset: monitor.getInitialSourceClientOffset(),
    currentOffset: monitor.getSourceClientOffset(),
    isDragging: monitor.isDragging(),
  }));

  if (!isDragging) {
    return null;
  }

  return (
    <Container>
      <StyledItemWrapper
        x={currentOffset?.x}
        y={currentOffset?.y}
        width={width}
        colorsMode={colorsMode}
      >
        <InfoRow>
          <Info>
            <StyledReorderIcon className="reorderIcon" color={theme.palette.common.blue} />
            <StyledTitle isSmall={isSmall}>{item?.title}</StyledTitle>
          </Info>
          <StyledOrderLabel>
            {Boolean(answerOrder?.length)
              ? t('common.orderKey', { count: answerOrder.indexOf(item?.id) + 1, ordinal: true })
              : ''}
          </StyledOrderLabel>
        </InfoRow>
      </StyledItemWrapper>
    </Container>
  );
};

export default CustomDragLayer;

const Container = styled('div')`
  position: fixed;
  pointer-events: none;
  z-index: 100;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
`;
const StyledItemWrapper = styled(ItemWrapper, {
  shouldForwardProp: (prop) =>
    prop !== 'x' && prop !== 'y' && prop !== 'width' && prop !== 'width' && prop !== 'colorsMode',
})<{
  x?: number;
  y?: number;
  width?: number;
  colorsMode?: QuizColorsMode;
}>`
  transform: translate(${({ x }) => x}px, ${({ y }) => y}px);
  background: ${({ theme, colorsMode = QuizColorsMode.PostVideo }) =>
    createAnswersThemes(theme)[colorsMode].selected};
  border: 3px solid ${({ theme }) => theme.palette.common.blue};
  width: ${({ width }) => width}px;
`;
const StyledOrderLabel = styled(OrderLabel)`
  background: ${({ theme }) => alpha(theme.palette.common.blue, 0.1)};
  color: ${({ theme }) => theme.palette.common.blue};
`;
